import { cn } from '@/lib/utils';
import {
  CHECKLIST_SECTIONS, SECTION_COLORS, SECTION_WEIGHTS, normaliseProgress, calcWeightedPct,
} from '@/components/AssemblyChecklist';

type UnitProgress = Record<string, boolean>;

/** Above this many units, per-unit rows stop fitting on a card; one aggregate
 *  row with a units-done count is shown instead. */
const MAX_UNIT_ROWS = 4;

/** Where a unit (or several, pooled) stands: the first section with work left,
 *  and how far into it. */
function whereAt(units: UnitProgress[]): string {
  const started = units.some(up => Object.values(up).some(Boolean));
  if (!started) return 'Not started';
  for (const section of CHECKLIST_SECTIONS) {
    const total = section.items.length * units.length;
    const done = units.reduce((n, up) => n + section.items.filter(i => up[i.key]).length, 0);
    if (done < total) return `${section.label} ${done}/${total}`;
  }
  return 'Done';
}

/** One bar: a segment per section, sized by its weight and filled by the
 *  share of its steps ticked across `units` — the checklist's own bar, at
 *  card size. */
function SectionBar({ units, className }: { units: UnitProgress[]; className?: string }) {
  return (
    <div className={cn('flex h-1.5 flex-1 gap-px overflow-hidden rounded-full', className)}>
      {CHECKLIST_SECTIONS.map(section => {
        const total = section.items.length * units.length;
        const done = units.reduce((n, up) => n + section.items.filter(i => up[i.key]).length, 0);
        return (
          <div
            key={section.key}
            className="h-full bg-muted"
            style={{ width: `${SECTION_WEIGHTS[section.key]}%` }}
            title={`${section.label}: ${done}/${total}`}
          >
            <div className={cn('h-full', SECTION_COLORS[section.key])} style={{ width: `${(done / total) * 100}%` }} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Checklist progress for a pipeline card: how far each unit has got, section
 * by section. The order's column only says which stage the least-advanced
 * unit is in; this shows where inside it, and which units are ahead.
 */
export function ChecklistProgress({ checklistProgress, quantity }: { checklistProgress: unknown; quantity: number }) {
  const unitCount = Math.max(quantity || 1, 1);
  const all = normaliseProgress(checklistProgress, unitCount);
  const units = Array.from({ length: unitCount }, (_, i) => all[String(i + 1)] ?? {});

  if (unitCount === 1 || unitCount > MAX_UNIT_ROWS) {
    const pct = Math.round(units.reduce((s, up) => s + calcWeightedPct(up), 0) / unitCount);
    const unitsDone = units.filter(up => whereAt([up]) === 'Done').length;
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <SectionBar units={units} />
          <span className="w-8 text-right text-[10px] tabular-nums text-muted-foreground">{pct}%</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          {whereAt(units)}
          {unitCount > 1 && ` · ${unitsDone}/${unitCount} units done`}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {units.map((up, i) => {
        const at = whereAt([up]);
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="w-5 text-[10px] font-medium text-muted-foreground">U{i + 1}</span>
            <SectionBar units={[up]} />
            <span
              className={cn(
                'w-20 truncate text-right text-[10px]',
                at === 'Done' ? 'text-green-700 dark:text-green-500' : 'text-muted-foreground',
              )}
              title={at}
            >
              {at}
            </span>
          </div>
        );
      })}
    </div>
  );
}
